/**
 * 파일명 : pjt0104m.tsx
 * 설명 :  프로젝트목록 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2023-03-06   정청운     최초작성
 * 1.1       2024-01-25   김시현     문구추가 및 상세보기버튼 비활성화 해제
 * 1.2       2024-03-19   이경원     조회조건 이벤트추가 ( 프로젝트명 : Enter키 이벤트 추가 , 
 *                                   PM : select박스 추가 (관리자일경우 select 박스 활성화, 관리자가 아닌 경우 : 로그인한 사용자이름 selected & 비활성화)     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Pjt0104mAction } from '../../../redux/slice/pjt/pjt0104m_slice';
import { IInquiryCndsState, IPjtItemState, IUserItemState, IProjectCdItemState } from '../../../redux/models/pjt/pjt0104m_model';


/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Collapse, Select, Table} from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import ExportExcelFile from 'components/exportExcelFile';

/************************************************
 * 전역변수 영역
 ************************************************/
const Pjt0104m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
     const dispatch = useAppDispatch();
     const current       = useAppSelector((state) => state.Pjt0104m.current);
     const pageSize      = useAppSelector((state) => state.Pjt0104m.pageSize);
     const inquiryCnds   = useAppSelector((state) => state.Pjt0104m.inquiryCnds);
     const userList      = useAppSelector((state) => state.Pjt0104m.userList);
     const projectCdList = useAppSelector((state) => state.Pjt0104m.projectCdList);
     const projectList   = useAppSelector((state) => state.Pjt0104m.projectList);

    const [sortInfo, setSortInfo] = React.useState<{
        order: 'ascend' | 'descend' | null;
        // columnKey: string | number | bigint;
        columnKey: keyof IPjtItemState;
    }>({
        order: 'ascend',
        columnKey: 'pjStartDt',
    });


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
     React.useEffect(() => {
        fn_PjtCds();    // 프로젝트전체조회
        fn_User();      // 사용자전체조회
        fn_Search();
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps


    /* Table Columns */
    const columns = React.useMemo(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>번호</div>,
                dataIndex: 'no',
                key: 'no',
                align: 'center',
                width: '5%',
                render: (_: any, __: IPjtItemState, index: number) => (current - 1) * pageSize + index + 1,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트코드</div>,
                dataIndex: 'pjCd',
                key: 'pjCd',
                align: 'center',
                width: '10%',
            },        
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트명</div>,
                dataIndex: 'pjNm',
                key: 'pjNm',
                align: 'left',
                width: '30%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (_:string, record:IPjtItemState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={() => {
                            showDetail2(record);
                        }}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}> 팀원관리</div>,
                key: 'action',
                render: (_:string, record:IPjtItemState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={() => {
                            showDetail(record);
                        }}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>Project Manager</div>,
                dataIndex: 'pmUserNm',
                key: 'pmUserNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>종료여부</div>,
                dataIndex: 'endYn',
                key: 'endYn',
                align: 'center',
                render: (text:string) => text === 'N' ? '부' : '여',
                width: '5%',
            },        
            {
                title: () => <div style={{ textAlign: 'center' }}>시작일자</div>,
                dataIndex: 'pjStartDt',
                key: 'pjStartDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                sorter: (a:IPjtItemState, b:IPjtItemState) => a.pjStartDt.localeCompare(b.pjStartDt),
                sortOrder: sortInfo.columnKey === 'pjStartDt' ? sortInfo.order : null,
                width: '8%',          
            },    
            {
                title: () => <div style={{ textAlign: 'center' }}>종료일자</div>,
                dataIndex: 'pjEndDt',
                key: 'pjEndDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '8%',           
            }];
            return arr;
    }, [sortInfo]);

    /**
     * 기능 : fn_User
     * 설명 : 사용자 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Pjt0104mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        const arrUserOptions = [{label: '전체', value: ''}];
        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);

    /**
     * 기능 : fn_PjtCds
     * 설명 : 사용자 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_PjtCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0002',
            success: (responseData:any) => {
                dispatch(Pjt0104mAction.setProjectCdList(responseData.projectList));
            },
        });
    };

    const pjtOptions = React.useCallback(() => {
        const arrOptions:IProjectCdItemState[] = [{label: '전체', value: ''}];
        projectCdList.forEach((item:IProjectCdItemState) => {
            if (!common.isAdmin()) {
                if (common.getEmpNo() === item.pmEmpNo) {
                    arrOptions.push({
                        label: item.pjNm,
                        value: item.pjCd
                    });
                }
            } else {
                arrOptions.push({
                    label: item.pjNm,
                    value: item.pjCd
                });
            }
        });

        return arrOptions;
    }, [projectCdList]);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Pjt0104mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Pjt0104mAction.setCurrent(1));
        dispatch(Pjt0104mAction.setProjectList([]));
    };

     /**
     *  기능 : 프로젝트 종료여부 콤보 변경
     *  설명 : 프로젝트 종료여부 콤보 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const endYn_onChange = (inputValue:string) =>{
        /*프로젝트 삭제여부로 추가 20230320*/
        dispatch(Pjt0104mAction.setInquiryCnds({ ...inquiryCnds, endYn : inputValue }));
    }

     /**
     *  기능 : 사용자 콤보 변경
     *  설명 : 사용자 콤보 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
     const userSelect = (inputValue:string) => {
        dispatch(Pjt0104mAction.setInquiryCnds({ ...inquiryCnds, pmEmpNo : inputValue }));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IPjtItemState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }
            
            case 'btn_New': {
                // common.openPage('프로젝트등록', 'pjt0102m',  record);
                const param = {
                    menuId : 'MPJT2023003',
                    props : {
                    }
                };
                common.openPage(param);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 상세보기
     * param : 
     */
    const showDetail2 = (record:IPjtItemState) => {        
        // common.openPage('프로젝트등록', 'pjt0102m',  record);     
        const param = {
            menuId : 'MPJT2023003',
            props : {
                pjCd : record.pjCd
            }
        };
        common.openPage(param);
    }

    /**
     * 기능 : 팀원관리
     * param : 
     */
    const showDetail = (record:IPjtItemState) => {
        const param = {
            menuId : 'MPJT2023004',
            props : {
                pjCd: record.pjCd
            }
        };
        common.openPage(param);
    }

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        fn_ResetResult();

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0002',
            success: (responseData:any) => {
                setSortInfo({
                    order: 'descend',
                    columnKey: 'pjStartDt',
                });

                dispatch(Pjt0104mAction.setProjectList(responseData.projectList));
            },
        });
    };

    /**
     * 기능 : 검색조건 프로젝트명 변경시 값 셋팅
     * @param 프로젝트 코드
     */
    const projectSelect = (inputValue:string) => {
        dispatch(Pjt0104mAction.setInquiryCnds({ ...inquiryCnds, pjNm : inputValue }));
    };
    /**
     * 기능 : 검색조회-Enter키를 눌렀을 때 실행
     * param : 
     */
    const handleOnKeyUp = (event:any) => {
        if (event.key === 'Enter') {
            // 엔터 키가 눌렸을 때 실행할 동작
            fn_Search();
        }
    };

    /* 엑셀다운로드 */
    const getSortedData = (): IPjtItemState[] => {
        if (!sortInfo.order || !sortInfo.columnKey) {
          return projectList; // 정렬 안된 원본
        }
      
        const sorted = [...projectList].sort((a, b) => {
          const valA = a[sortInfo.columnKey];
          const valB = b[sortInfo.columnKey];
      
          // 날짜 필드인 경우 (예: pjStartDt, pjEndDt)
          if (typeof valA === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valA)) {
            return sortInfo.order === 'ascend'
              ? new Date(valA).getTime() - new Date(valB).getTime()
              : new Date(valB).getTime() - new Date(valA).getTime();
          }
      
          // 숫자 또는 일반 문자열
          if (typeof valA === 'number' && typeof valB === 'number') {
            return sortInfo.order === 'ascend' ? valA - valB : valB - valA;
          }
      
          return sortInfo.order === 'ascend'
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });
      
        return sorted;
    };

    const excelParams = {
        datas: getSortedData().map((item, index) => ({
            ...item, no: index + 1
        })),
        btnName: '엑셀파일 내려받기',
        fileName: '프로젝트내역_' + commonDate.getTodayFormat('YYYYMMDD'),
        sheetName:'프로젝트내역_' + commonDate.getTodayFormat('YYYYMMDD'),
        columns: columns,
    }

    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space>
                                        <Space>
                                            <label>프로젝트명</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 400,
                                                }}
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                    
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                    
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={pjtOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.pjCd}
                                                onChange={(_, record:any) => {
                                                    dispatch(Pjt0104mAction.setInquiryCnds({...inquiryCnds, pjCd: record.value, pjNm : common.isEmptyString(record.value) ? '' : record.label}))
                                                }}
                                            ></Select>
                                        </Space>
                                        <Space>
                                            <label>Project Manager</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 120,
                                                }}
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                    
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                    
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.pmEmpNo}
                                                onChange={val => dispatch(Pjt0104mAction.setInquiryCnds({...inquiryCnds, pmEmpNo: val}))}
                                                // ReadOnly 적용
                                                {...common.getReadonlySelectboxProps(!common.isAdmin())}
                                            ></Select>
                                        </Space>
                                        <Space>
                                            <label>종료여부</label>
                                            <Select id='inputEndYn' style={{ width: 100}} value={inquiryCnds.endYn} defaultValue='' onChange={endYn_onChange}>
                                                <option value=''>전체</option>
                                                <option value='Y'>여</option>
                                                <option value='N'>부</option>
                                            </Select>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                    <Button
                                            name='btn_New'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            신규
                                    </Button>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            초기화
                                    </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            조회
                                    </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5' >
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>프로젝트내역</Text><Text style={{color: 'blue'}}>(※ PM 및 관리자만 조회 가능합니다.)</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5' style={{ marginTop: 5 }}>
                            <ExportExcelFile excelParams={excelParams}/>
                        </Col>
                    </Row>
                    <Table dataSource={projectList}
                        key='no'
                        columns={columns}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + projectList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Pjt0104mAction.setCurrent(page));
                                dispatch(Pjt0104mAction.setPageSize(pageSize));
                            },
                        }}
                        onChange={(_pagination, _filters, sorter) => {
                            if (!Array.isArray(sorter)) {
                                setSortInfo({
                                  order: sorter.order ?? null,
                                  columnKey: sorter.columnKey as keyof IPjtItemState,
                                });
                            }
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Pjt0104m;
